import { createFormatForDescription as cffd } from '../createFormatForDescription'
import { Explanation, explain, explainOptional } from '../explain/Explanation'
import EscrowFinishInstructions from '../types/instructions/EscrowFinishInstructions'
import TransactionType from '../types/TransactionType'

/**
 * An annotation for EscrowFinish transactions.
 *
 * @property type - The type of the transaction.
 * @property offerSequence - An explanation of the offerSequence.
 * @property owner - An explanation of the owner.
 * @property condition - An explanation of the condition.
 * @property fulfillment - An explanation of the fulfillment.
 */
export interface EscrowFinishAnnotation {
  readonly type: TransactionType.EscrowFinish
  readonly offerSequence: Explanation<number>
  readonly owner: Explanation<string>
  readonly condition?: Explanation<string>
  readonly fulfillment?: Explanation<string>
}

/**
 * Takes a transaction object and returns an annotated transaction object.
 * The returned object contains explanations about the various transaction features.
 *
 * @param transactionInstructions - The input transaction.
 * @returns An annotated transaction object detailing the various transaction features.
 */
export function annotate(
  transactionInstructions: EscrowFinishInstructions,
): EscrowFinishAnnotation {
  const offerSequence = explain(
    transactionInstructions.OfferSequence,
    `Cancel the escrow transaction with transaction sequence ${cffd(
      transactionInstructions.OfferSequence.toString(),
      'offerSequence',
    )}`,
  )

  const owner = explain(
    transactionInstructions.Owner,
    `The original address that funded the escrow transaction as ${cffd(
      transactionInstructions.Owner,
      'owner',
    )}`,
  )

  const condition = explainOptional(
    transactionInstructions.Condition,
    (theCondition) =>
      `The hex value matching the previously-supplied PREIMAGE-SHA-256 crypto-condition of the held payment is ${cffd(
        theCondition,
        'condition',
      )}`,
  )

  const fulfillment = explainOptional(
    transactionInstructions.Fulfillment,
    (theFulfillment) =>
      `Hex value of the PREIMAGE-SHA-256 crypto-condition fulfillment matching the held payment's Condition is ${cffd(
        theFulfillment,
        'fulfillment',
      )}`,
  )

  return {
    offerSequence,
    owner,
    ...(condition && { condition }),
    ...(fulfillment && { fulfillment }),

    type: TransactionType.EscrowFinish,
  }
}
